/**
 * F27 (Wave 2E) — admin WhatsApp quality state page.
 *
 * Route: `/whatsapp/quality`
 * Permission: `whatsapp:read` (super_admin + support + finance + content_moderator).
 *
 * Renders the singleton snapshot of Meta's sender-number quality state +
 * a placeholder for the 30-day history table (deferred to F45 admin analytics).
 *
 * State semantics:
 *   - GREEN  → success badge
 *   - YELLOW → warning badge
 *   - RED    → danger badge (alerts already fired via the quality monitor)
 *   - status CONNECTED → success; FLAGGED/RESTRICTED/PENDING_REVIEW → danger
 *
 * Layout mirrors F23 commission detail + F28 email-suppressions: header +
 * Card + dl-style key/value grid.
 */
import { useCallback, useEffect, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import { Badge, Banner, Card, EmptyState, Skeleton } from '@bravemove/ui-web';
import { UIIcon } from '@bravemove/design-tokens/icons';
import type {
  WhatsAppPhoneNumberStatus,
  WhatsAppQualityRating,
  WhatsAppQualityState,
} from '@bravemove/types';
import { getWhatsAppQuality } from '../../../api/whatsapp';
import { translateApiError } from '../../../auth/errors';
import { PageHeader } from '../../../components/PageHeader';

function ratingTone(
  rating: WhatsAppQualityRating | null,
): 'success' | 'warning' | 'danger' | 'neutral' {
  if (rating === null || rating === 'UNKNOWN') return 'neutral';
  switch (rating) {
    case 'GREEN':
      return 'success';
    case 'YELLOW':
      return 'warning';
    case 'RED':
      return 'danger';
    default:
      return 'neutral';
  }
}

function statusTone(
  status: WhatsAppPhoneNumberStatus,
): 'success' | 'warning' | 'danger' | 'neutral' {
  switch (status) {
    case 'CONNECTED':
      return 'success';
    case 'PENDING_REVIEW':
      return 'warning';
    case 'FLAGGED':
    case 'RESTRICTED':
    case 'DISCONNECTED':
      return 'danger';
    default:
      return 'neutral';
  }
}

function formatDate(iso: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

interface QualityDataLineProps {
  readonly labelKey: string;
  readonly defaultLabel: string;
  readonly children: React.ReactNode;
  readonly testIdSuffix?: string;
}

function QualityDataLine(props: QualityDataLineProps) {
  const { t } = useI18n();
  return (
    <>
      <dt className="text-ink-3 font-mono uppercase tracking-wider text-[10.5px]">
        {t(props.labelKey, { defaultValue: props.defaultLabel })}
      </dt>
      <dd
        className="text-ink"
        data-testid={props.testIdSuffix ? `whatsapp-quality-${props.testIdSuffix}` : undefined}
      >
        {props.children}
      </dd>
    </>
  );
}

export function WhatsAppQualityPage() {
  const { t, locale } = useI18n();
  const [current, setCurrent] = useState<WhatsAppQualityState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchState = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getWhatsAppQuality();
      setCurrent(result.current);
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchState();
  }, [fetchState]);

  return (
    <div className="space-y-6" data-testid="whatsapp-quality-page">
      <PageHeader
        title={t('admin:whatsapp.quality.page_title', { defaultValue: 'WhatsApp quality' })}
        subtitle={t('admin:whatsapp.quality.page_description', {
          defaultValue:
            'Daily snapshot of Meta’s quality rating + messaging tier + sender-number status for the BraveMove WhatsApp Business number.',
        })}
      />

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {loading ? (
        <div className="space-y-3" data-testid="whatsapp-quality-loading">
          <Skeleton className="h-40 w-full rounded-lg" />
        </div>
      ) : current === null ? (
        <EmptyState
          icon={<UIIcon name="bell" size={36} color="currentColor" />}
          title={t('admin:whatsapp.quality.no_state_title', {
            defaultValue: 'No quality state recorded yet',
          })}
          description={t('admin:whatsapp.quality.no_state_description', {
            defaultValue:
              'The quality monitor worker runs daily at 09:05 Asia/Beirut. First run will populate this view.',
          })}
        />
      ) : (
        <Card>
          <div className="flex flex-col gap-3">
            <h2 className="text-h2 font-bold text-ink">
              {t('admin:whatsapp.quality.current_card_title', { defaultValue: 'Current state' })}
            </h2>
            <dl className="grid grid-cols-[160px_1fr] gap-y-2 gap-x-3 text-[12.5px]">
              <QualityDataLine
                labelKey="admin:whatsapp.quality.fields.phone_number_id"
                defaultLabel="Phone number ID"
                testIdSuffix="phone-number-id"
              >
                <span className="font-mono">{current.phoneNumberId}</span>
              </QualityDataLine>
              <QualityDataLine
                labelKey="admin:whatsapp.quality.fields.display_phone_number"
                defaultLabel="Display phone number"
                testIdSuffix="display-phone-number"
              >
                {current.displayPhoneNumber}
              </QualityDataLine>
              <QualityDataLine
                labelKey="admin:whatsapp.quality.fields.verified_name"
                defaultLabel="Verified business name"
              >
                {current.verifiedName ?? '—'}
              </QualityDataLine>
              <QualityDataLine
                labelKey="admin:whatsapp.quality.fields.quality_rating"
                defaultLabel="Quality rating"
                testIdSuffix="quality-rating"
              >
                {current.qualityRating ? (
                  <Badge tone={ratingTone(current.qualityRating)} size="sm">
                    {t(`admin:whatsapp.quality.rating.${current.qualityRating}`, {
                      defaultValue: current.qualityRating,
                    })}
                  </Badge>
                ) : (
                  '—'
                )}
              </QualityDataLine>
              <QualityDataLine
                labelKey="admin:whatsapp.quality.fields.messaging_limit_tier"
                defaultLabel="Messaging tier"
              >
                {current.messagingLimitTier
                  ? t(`admin:whatsapp.quality.tier.${current.messagingLimitTier}`, {
                      defaultValue: current.messagingLimitTier,
                    })
                  : '—'}
              </QualityDataLine>
              <QualityDataLine
                labelKey="admin:whatsapp.quality.fields.status"
                defaultLabel="Status"
                testIdSuffix="status"
              >
                <Badge tone={statusTone(current.status)} size="sm">
                  {t(`admin:whatsapp.quality.status_values.${current.status}`, {
                    defaultValue: current.status,
                  })}
                </Badge>
              </QualityDataLine>
              <QualityDataLine
                labelKey="admin:whatsapp.quality.fields.last_synced_at"
                defaultLabel="Last synced"
              >
                <span className="font-mono tabular-nums text-[11.5px]">
                  {formatDate(current.lastSyncedAt, locale)}
                </span>
              </QualityDataLine>
            </dl>
          </div>
        </Card>
      )}

      <Card>
        <div className="flex flex-col gap-2">
          <h2 className="text-h2 font-bold text-ink">
            {t('admin:whatsapp.quality.history_card_title', { defaultValue: '30-day history' })}
          </h2>
          <Banner tone="info">
            {t('admin:whatsapp.quality.history_placeholder', {
              defaultValue: 'Per-day quality-rating history ships with F45 admin analytics.',
            })}
          </Banner>
        </div>
      </Card>
    </div>
  );
}

export default WhatsAppQualityPage;
